import { nextTick } from 'vue'
import { useHttpStore } from '@/js/stores/httpStore'
import type { ReflektionToken } from '@types'
import { encodeRfkPayload, processRfkFacet, processRfkProduct } from './util'
import {
  IReflektionPayloadOptions,
  IReflektionProcessedResponse,
  IReflektionRawResponse,
  widgets,
} from './types'
import { Rogue } from '../Rogue'
import type { IRogueRfk } from '../RogueTypes'

export class RogueRfk implements IRogueRfk {
  tokens: ReflektionToken | undefined

  rogue: Rogue | undefined

  refreshPromise?: Promise<ReflektionToken>

  inFlight: { [key: string]: Promise<IReflektionProcessedResponse> } = {}

  constructor(tokens?: ReflektionToken) {
    if (tokens) {
      tokens.apiUrl = tokens.apiUrl.replace('apigw.rfk.www.', 'apigw.rfk.')
      this.tokens = tokens
    }
  }

  init(rogue: Rogue) {
    this.rogue = rogue

    if (!rogue.ssr) {
      // Start a request to get valid tokens, so they will be available when needed
      nextTick(() => this.getValidTokens())
    }
  }

  /**
   * Make sure the current tokens are present and valid for at least another minute
   */
  hasValidTokens(): boolean {
    return this.tokens !== undefined && new Date(this.tokens.expires).getTime() > (Date.now() + 1000 * 60)
  }

  getValidTokens(attempt = 0): Promise<ReflektionToken> {
    // If we already have unexpired tokens, just use those
    if (this.hasValidTokens() && this.tokens !== undefined) {
      return Promise.resolve(this.tokens)
    }
    const rogue = this.rogue

    if (!rogue) {
      throw new Error('Unable to load tokens, init not called')
    }

    // Multiple calls might all try to get tokens simultaneously, but we only want to make one backend request
    if (!this.refreshPromise) {
      this.refreshPromise = rogue.api.fetch<ReflektionToken>({
        url    : `/svc_ssr/site/${rogue.site.code}/rfktkn${attempt > 0 ? `?ts=${Date.now()}` : ''}`,
        method : 'GET',
        cache  : { key: 'rfktkns', seconds: 60 * 15 },
      })
        .then(tokens => {
          if (typeof tokens === 'string') {
            this.rogue?.log.warn('Retrieved invalid RFK tokens - expected object, got a string', { value: `${(tokens as string).substring(0, 50)}...` })
          }
          this.tokens = tokens
          rogue.log.crumb('rfk', 'Got new tokens')
          if (!this.hasValidTokens()) {
            // This is an error state - it means we received tokens from the server that are already invalid :(
            return Promise.reject('Expired tokens')
          }

          this.refreshPromise = undefined

          return Promise.resolve(this.tokens)
        }).catch(err => {
          rogue.log.crumb('rfk', `Error getting tokens (attempt ${attempt})`)
          if (attempt < 5 && !rogue.ssr) {
            /**
             * Try again in exponential backoff to get valid tokens
             * Exponential Backoff: (1/2) * (2^c - 1) * 250
             * 125ms, 375ms, 875ms, 1875ms, etc
             */
            const backoff = 0.5 * ((2 ** attempt) - 1) * 250

            return new Promise(resolve => setTimeout(() => {
              rogue.log.info(`Getting new RFK tokens after ${backoff}ms (attempt ${attempt + 1})`)
              this.refreshPromise = undefined
              resolve(this.getValidTokens(attempt + 1))
            }, backoff))
          }

          // After five retry attempts, fail the original request
          rogue.log.warn('Error refreshing RFK tokens', err.response?.data || err.code || err)
          if (err.response) {
            console.log(err.response.headers, err.response.body, err.response.data)
          }

          return Promise.reject('Could not get valid tokens')
        })
    }

    return this.refreshPromise
  }

  getCacheKeyForPayload(payload: IReflektionPayloadOptions) {
    const cacheKeyParts = ['rfk']

    if (payload.widget !== widgets.CATEGORY) {
      cacheKeyParts.push(`w_${payload.widget}`)
    }

    if (payload.page_number && payload.page_number !== 1) {
      cacheKeyParts.push(`p_${payload.page_number || 1}`)
    }

    if ([widgets.CATEGORY, widgets.PRODUCTS_SIMILAR].includes(payload.widget)) {
      cacheKeyParts.push(payload.uri)
    }

    cacheKeyParts.push(`count_${payload.n_item}`)

    if (payload.query) {
      cacheKeyParts.push(`q_${payload.query}`)
    }

    if (payload.sort && payload.sort.value?.[0] && payload.sort.value[0].type !== 'featured') {
      cacheKeyParts.push(`s_${payload.sort.value[0].type}-${payload.sort.value[0].order}`)
    }

    // Normalize filter ordering so permutation-equivalent requests share a
    // cache entry: sort keys alphabetically, and sort array values within each
    // key. ?manufacturer=A&manufacturer=B and ?manufacturer=B&manufacturer=A
    // produce the same RFK response — they should produce the same cache key.
    const sortedFilterEntries = Object.entries(payload.filter || {})
      .sort(([a], [b]) => a.localeCompare(b))
    sortedFilterEntries.forEach(([key, value]) => {
      const normalized = Array.isArray(value) ? [...value].sort() : value
      cacheKeyParts.push(`f_${key}_${JSON.stringify(normalized || '')}`)
    })

    return cacheKeyParts.join('-')
  }

  /**
   * Load data from RFK. This function uses promises instead of async / await because we often
   * want it to return synchronously - for SSR hydration - so we need to return something OTHER than a promise.
   * @param payload - RFK response payload
   * @param attempt - Retry attempt number
   */
  fetch(payload: IReflektionPayloadOptions, attempt = 0) {
    payload.n_item = payload.n_item || 80

    // We want the SSR server to cache all RFK responses, because it gets the same data every time
    const cacheKey = this.getCacheKeyForPayload(payload)

    if (this.inFlight[cacheKey] !== undefined) {
      return this.inFlight[cacheKey]
    }

    const rogue = this.rogue

    if (!rogue) {
      throw new Error('Init not called')
    }

    const httpStore = useHttpStore(rogue.pinia)

    let cacheContent = httpStore.cache
    if (rogue.ssrContext && rogue.ssrContext?.renderUrl?.indexOf('FLUSH_CAT_CACHE') > -1) {
      rogue.log.info(`Flushing category cache: ${cacheKey}`)
      httpStore.clearCache(cacheKey)

      // update to remove the key that got cleared
      cacheContent = httpStore.cache
    }

    // See if the SSR already found and loaded this data. If so, we dont need to re-load it
    if (!rogue.ssr && cacheContent[cacheKey]) {
      const data = cacheContent[cacheKey] as IReflektionProcessedResponse

      rogue.log.info(`Loading RFK data from SSR-supplied Initial State: ${cacheKey}`)

      if (payload.widget === widgets.PRODUCTS_FOR_YOU) {
        // We may have had to cache generic FOR YOU to use for hydration, but from here out we want to get *real* For You data
        cacheContent[cacheKey] = undefined
      }

      return data
    }

    /**
     * If this is an SSR load test, we dont want to make new RFK calls - or else we would be load testing Reflektion
     * instead of our own servers
     */
    if (rogue.ssrContext?.isLoadTest && rogue.ssr) {
      if (payload.widget === widgets.CATEGORY) {
        /**
         * For category page load tests, EVERY category will show the "New Gear" data (and cache in Redis)
         */
        payload.uri = '/new-gear'
        payload.query = undefined
        payload.filter = undefined
      } else {
        // Load tests should not load any other widget sections
        return {
          content     : { product: { n_item: 0, value: [], total_item: 0 } },
          facet       : {},
          facet_names : [],
          n_item      : 0,
          page_number : 0,
          total_item  : 0,
          total_page  : 0,
          url         : '/',
          widget      : { rfkid: '' },
        } as IReflektionProcessedResponse
      }
    }

    // Cache RFK responses in Redis with a 10-minute soft TTL. Cache key is
    // keyed on widget + uri + filter + sort + page; no user identity, so the
    // cache is safe to share. Negative responses (errors=[uri_not_found])
    // cache automatically at the same TTL — the rejection in the .then()
    // below happens after SSRHttpInstance has already written the response.
    //
    // fallbackOnError: true enables stale-on-error semantics — past 10 min,
    // a stale value is served instantly while a background refresh runs. If
    // the refresh fails (RFK outage), the stale value continues serving.
    // Hard Redis eviction defaults to 7 days.
    //
    // SEARCH_RESULTS is intentionally excluded: search queries are typically
    // unique per user and expected to reflect the freshest catalog state.
    // Featured-URI search responses are still cached because those are
    // editorially-curated and stable.
    const cacheTtlSeconds = 10 * 60
    const shouldCacheResponse = rogue.ssrContext?.isLoadTest
      || payload.widget !== widgets.SEARCH_RESULTS
      || payload.uri.indexOf('featured') !== -1

    this.inFlight[cacheKey] = this.getValidTokens()
      .then(({ apiUrl, token }) => rogue.api.fetch<IReflektionRawResponse>({
        url     : apiUrl,
        method  : 'POST',
        data    : { data: encodeRfkPayload(rogue, payload) },
        headers : { Authorization: `Bearer ${token}` },
        // hardTtlSeconds is 1 hour for RFK: high cardinality (one entry per
        // filter+sort+page permutation) was filling Redis under crawler load.
        // Stale-on-error window shrinks from ~7 days to 50 minutes (1h hard −
        // 10m soft), still long enough to ride out typical RFK degradations.
        // BV intentionally keeps the 7-day default — lower volume, larger
        // cache value, longer outage coverage useful for review data.
        cache   : shouldCacheResponse
          ? { key: cacheKey, seconds: cacheTtlSeconds, fallbackOnError: true, hardTtlSeconds: 60 * 60 }
          : undefined,
      }))
      .then(rawResponse => {
        // @ts-ignore
        const response = rawResponse as IReflektionProcessedResponse

        delete this.inFlight[cacheKey]
        /**
         * If this was a category page, but RFK doesnt have a feed for it, they return status 200 and a list of ALL products.
         * We need to check the `errors` array and manually trigger an error
         */
        if (payload.widget === widgets.CATEGORY && response.errors?.some(e => e.error === 'uri_not_found' || e.type === 'uri_not_found')) {
          return Promise.reject(new Error('category_not_found'))
        }

        // Turn the RFK product entities into something more useful (that looks like m2-content products)
        response.content.product.value = rawResponse.content?.product?.value?.map(p => processRfkProduct(p, rogue))

        if (payload.widget === widgets.CATEGORY || payload.widget === widgets.SEARCH_RESULTS) {
          response.facet = response.facet || {}

          // Process the RFK facets - do things like replace text and format for Category page
          Object.keys(response.facet)
            .forEach(key => response.facet[key] = processRfkFacet(key, response.facet[key], rogue))

          // If there were no results, it might be because we searched for a bad filter parameter (like promotions=asdf)
          // We need to add that facet back (with 0 results) so they can deselect it
          if (response.content?.product?.value?.length === 0) {
            try {
              // Add empty responses for query params that they asked for but weren't returned...
              Object.keys(payload.filter || {})
                .forEach(key => {
                  if (!response.facet[key]) {
                    response.facet[key] = {
                      display_name       : key,
                      key,
                      number_of_products : 0,
                      total              : 0,
                      value              : [],
                    }
                  }
                  const values = response.facet[key].value
                  if (payload.filter) {
                    payload.filter[key].value
                      .filter(val => !values.some(v => v.urlKey === val))
                      .map(str => values.push({ label: str, urlKey: str, count: 0, id: str, text: str }))
                  }
                })
            } catch (e) {
              rogue.log.warn('Error adding active facets', { query: payload.filter, error: e })
            }
          }
        }

        /**
         * If the SSR loaded RFK data, we need to save it to the store so that the client can use it synchronously
         * during hydration.
         */
        if (rogue.ssr) {
          httpStore.setCache(cacheKey, response)
        }

        return response
      }).catch(err => {
        delete this.inFlight[cacheKey]
        if (err.message === 'browser_offline' || err.code === 'UNABLE_TO_GET_ISSUER_CERT_LOCALLY') {
          return Promise.reject(err)
        }

        rogue.log.info(`Error loading RFK data for ${payload.uri} (attempt ${attempt})`, err.response?.data || err.code)

        const isCategoryNotFound = err.message === 'category_not_found' || err === 'category_not_found'

        if (!isCategoryNotFound && attempt < 3 && err.message !== 'no_response') {
          attempt += 1

          // Retry this request
          return new Promise(resolve => setTimeout(() => resolve(this.fetch(payload, attempt)), attempt * 100))
        }

        // We dont want to cache renders that had RFK errors
        if (rogue.ssrContext?.renderData) {
          rogue.ssrContext.renderData.cache.disable = true

          // We need to store this in the state for SPA, so it doesnt have a hydrate error trying to reload it
          if (isCategoryNotFound) {
            httpStore.setCache(cacheKey, { error: 'category_not_found' })
          }
        }

        return Promise.reject(err)
      })

    return this.inFlight[cacheKey]
  }
}
